import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Stack, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddIcon from "@mui/icons-material/Add";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, DataTable, StatusChip, useConfirm } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { extractErrorMessage } from "../../utils/errorMessage";

const money = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

export default function InventoryListNew() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const { hasRole } = useAuth();
  const canManage = hasRole("admin");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/inventory");
      setItems(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const inStock = items.filter((i) => i.status === "In Stock").length;
    const assigned = items.filter((i) => i.assignedTo || i.status === "Assigned").length;
    const totalValue = items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseFloat(i.stock) || 1), 0);
    return { total, inStock, assigned, totalValue };
  }, [items]);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))], [items]);

  const handleDelete = async (itemID) => {
    const ok = await confirm({ title: "Delete item?", description: `Inventory #${itemID} will be permanently removed.`, confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/api/inventory/${itemID}`);
      toast.success("Item removed");
      load();
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const columns = [
    { field: "itemID", label: "ID", render: (r) => <Typography variant="caption" sx={{ fontFamily: "monospace", fontWeight: 700 }}>{r.itemID}</Typography> },
    { field: "itemName", label: "Item", render: (r) => (
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{r.itemName}</Typography>
        {r.brandModel && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{r.brandModel}</Typography>}
      </Box>
    )},
    { field: "category", label: "Category", render: (r) => <Chip size="small" label={r.category || "—"} variant="outlined" /> },
    { field: "serialNumber", label: "Serial", render: (r) => <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{r.serialNumber || "—"}</Typography> },
    { field: "status", label: "Status", render: (r) => <StatusChip status={r.status || "In Stock"} /> },
    { field: "assignedTo", label: "Assigned", render: (r) => r.assignedTo ? <Chip size="small" label={r.assignedToName ? `${r.assignedToName} (${r.assignedTo})` : r.assignedTo} /> : "—" },
    { field: "price", label: "Price", align: "right", render: (r) => money(r.price) },
    { field: "stock", label: "Qty", align: "right" },
    ...(canManage ? [{
      field: "_actions", label: "", render: (r) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={(e) => { e.stopPropagation(); nav(`/inventoryform?edit=${r.itemID}`); }}>
            <EditOutlinedIcon fontSize="small" />
          </Button>
          <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(r.itemID); }}>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </Button>
        </Stack>
      ),
    }] : []),
  ];

  return (
    <Box p={3}>
      <PageHeader
        title="Inventory"
        subtitle={`${items.length} items tracked`}
        icon={<Inventory2OutlinedIcon />}
        actions={canManage ? [
          <Button key="assign" variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => nav("/AssignInventoryToEmployee")}>
            Assign
          </Button>,
          <Button key="new" variant="contained" startIcon={<AddIcon />} onClick={() => nav("/inventoryform")}>
            Add Item
          </Button>,
        ] : undefined}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        <StatCard label="Total Items" value={stats.total} accent="#6C50BF" />
        <StatCard label="In Stock" value={stats.inStock} accent="#39C645" />
        <StatCard label="Assigned" value={stats.assigned} accent="#5C85FF" />
        <StatCard label="Est. Value" value={money(stats.totalValue)} hint="Stock × price" accent="#F9BC06" />
      </Box>

      <DataTable
        rows={items}
        columns={columns}
        loading={loading}
        onRowClick={(r) => nav(`/inventorydetail?id=${r.itemID}`)}
        filters={[
          { key: "cat", label: "Category", field: "category", options: categories.map((c) => ({ value: c, label: c })) },
          { key: "status", label: "Status", field: "status", options: ["In Stock", "Assigned", "Under Maintenance", "Retired", "Lost"].map((s) => ({ value: s, label: s })) },
        ]}
        emptyTitle="No inventory"
        exportFilename="inventory.csv"
        defaultSort={{ field: "itemID", direction: "desc" }}
        defaultPageSize={25}
      />
    </Box>
  );
}
